import {
  useQuery,
  useQueryClient,
  type DefaultError,
  type FetchQueryOptions,
  type QueryKey,
} from '@tanstack/react-query';
import { featureFlagQueryOptions } from '#api/org.queries.ts';
import type { FeatureFlags } from '#dn-types/org.ts';

// Vendored from https://tanstack.com/query/latest/docs/framework/react/guides/prefetching

/** This version uses the suspense-friendly prefetching that happens in the render path, rather than after suspense finishes. */
export function usePrefetchSuspenseQuery<
  TQueryFnData,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(options: FetchQueryOptions<TQueryFnData, TError, TData, TQueryKey>, requiredFeatureFlagIdent?: keyof FeatureFlags) {
  const queryClient = useQueryClient();
  const featureFlags = useQuery(featureFlagQueryOptions).data;

  // If we are gated on a FF && we haven't loaded FF's yet, don't fetch.
  if (!featureFlags && requiredFeatureFlagIdent) {
    return;
  }

  // This happens in render, but is safe to do because ensureQueryData
  // only fetches if there is no data in the cache for this query. This
  // means we know no observers are watching the data so the side effect
  // is not observable, which is safe.
  //
  // Exception: if the query is already in an error state, calling
  // ensureQueryData again refires the fetch on every render. During an
  // auth-failure burst (errored query + Suspense thrash) this produced a
  // 401 storm that tripped the rate limiter in prod. Bail out so the
  // error stays sticky until the auth flow resolves.
  const status = queryClient.getQueryState(options.queryKey)?.status;
  if (status === 'error') return;

  if (!requiredFeatureFlagIdent || featureFlags?.[requiredFeatureFlagIdent]) {
    void queryClient.ensureQueryData(options);
  }
}
